import { ValueOf, State, StateValues } from './state.enum';

/**
 * Modello stati normalizzato (RFC stato-colonnine §4): tre concetti, tre enum,
 * un owner ciascuno, al posto dell'unico enum numerico `State` usato per tutto.
 *
 * Valori STRINGA (decisione §10.14): i numerici non costano memoria, sono solo
 * poco chiari. Il codice numerico resta la rappresentazione delle COLONNE di
 * stato (`connector.state`, `station.state`, `installation.state`, `session.state`)
 * e dei payload verso le app: la conversione fra le due sta in fondo a questo file.
 */

/**
 * Stato FISICO del connettore — 1:1 con OCPP StatusNotification, nessuna
 * ambiguità. Owner: evse-state-service. È l'attuale `innerState` promosso a
 * unica rappresentazione fisica.
 */
export const PhysicalStatus = Object.freeze({
  Available: 'Available',
  Preparing: 'Preparing',
  Charging: 'Charging',
  SuspendedEV: 'SuspendedEV',
  SuspendedEVSE: 'SuspendedEVSE',
  Finishing: 'Finishing',
  Reserved: 'Reserved',
  Unavailable: 'Unavailable',
  Faulted: 'Faulted',
} as const);
export type PhysicalStatusValues = ValueOf<typeof PhysicalStatus>;

/**
 * Lifecycle della SESSIONE — vive sull'entità Session, owner: backend
 * (SessionLifecycleService, unico writer). OCCUPIED = erogazione finita,
 * veicolo attaccato (`stoppedAt` valorizzato): occupazione fatturabile,
 * reversibile al riattacco.
 */
export const SessionState = Object.freeze({
  RESERVED: 'RESERVED',
  PENDING: 'PENDING',
  PENDING_AUTH: 'PENDING_AUTH',
  ACTIVE: 'ACTIVE',
  OCCUPIED: 'OCCUPIED',
  ENDING: 'ENDING',
  ENDED: 'ENDED',
} as const);
export type SessionStateValues = ValueOf<typeof SessionState>;

/**
 * Stato AGGREGATO (stazione/installazione) — ristretto e sensato per la mappa.
 * Owner: evse-state-service. Niente CHARGING/PENDING a questo livello: gli
 * stati illegali sono irrappresentabili, non solo improbabili.
 */
export const AggregateState = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  OCCUPIED: 'OCCUPIED',
  RESERVED: 'RESERVED',
  UNAVAILABLE: 'UNAVAILABLE',
  OFFLINE: 'OFFLINE',
} as const);
export type AggregateStateValues = ValueOf<typeof AggregateState>;

// ---------------------------------------------------------------------------
// Validatori VERI (RFC §12.21): valore sconosciuto → undefined, mai cast cieco
// alla `asState` (che accetta anche 999). Il chiamante decide se flag/skip.
// ---------------------------------------------------------------------------

const PHYSICAL_BY_LOWER: ReadonlyMap<string, PhysicalStatusValues> = new Map(
  Object.values(PhysicalStatus).map((v) => [v.toLowerCase(), v]),
);
const SESSION_SET: ReadonlySet<string> = new Set(Object.values(SessionState));
const AGGREGATE_SET: ReadonlySet<string> = new Set(Object.values(AggregateState));

/** Case-insensitive (i firmware variano), ritorna il valore canonico. */
export function parsePhysicalStatus(value: unknown): PhysicalStatusValues | undefined {
  if (typeof value !== 'string') return undefined;
  return PHYSICAL_BY_LOWER.get(value.trim().toLowerCase());
}

export function parseSessionState(value: unknown): SessionStateValues | undefined {
  return typeof value === 'string' && SESSION_SET.has(value) ? (value as SessionStateValues) : undefined;
}

export function parseAggregateState(value: unknown): AggregateStateValues | undefined {
  return typeof value === 'string' && AGGREGATE_SET.has(value) ? (value as AggregateStateValues) : undefined;
}

// ---------------------------------------------------------------------------
// La derivazione fisico+sessione → aggregato vive QUI e solo qui (RFC §4.3).
// ---------------------------------------------------------------------------

/**
 * Sessioni VIVE: il connettore NON è libero, qualunque cosa dica il fisico. Serve perché il
 * fisico OCPP torna `Available` mentre la sessione è ancora viva — è il caso dell'OCCUPAZIONE
 * (erogazione finita, veicolo attaccato, timer che fattura), ma anche di ACTIVE/PENDING con
 * firmware che riportano Available. ENDING/ENDED/null NON sono vive: lì decide il fisico.
 */
const LIVE_SESSION: ReadonlySet<SessionStateValues> = new Set<SessionStateValues>([
  SessionState.RESERVED,
  SessionState.PENDING,
  SessionState.PENDING_AUTH,
  SessionState.ACTIVE,
  SessionState.OCCUPIED,
]);

/**
 * Contributo aggregato di UN connettore. Precedenza, dall'alto — la STESSA di
 * `computeConnectorState` (guasto > sessione > fisico): se le due divergono, la stazione dice
 * "Disponibile" mentre il connettore sotto dice "Occupazione".
 * 1. Faulted/Unavailable → UNAVAILABLE — il guasto vince su tutto, prenotazione inclusa.
 * 2. Sessione RESERVED o fisico Reserved → RESERVED. La prenotazione nativa NON manda
 *    comandi alla colonnina: il fisico resta Available e decide la sessione.
 * 3. Fisico Available SENZA sessione viva → AVAILABLE. Con una sessione viva sopra (OCCUPIED,
 *    ACTIVE, PENDING, PENDING_AUTH) il connettore è occupato: il fisico `Available` di fine
 *    erogazione non lo rende libero.
 * 4. Fisico ignoto → UNAVAILABLE: mai "disponibile" per assenza di informazione (nemmeno con
 *    sessione viva: l'aggregato è per la mappa, e senza fisico non si asserisce nulla).
 * 5. Resto (Preparing, Charging, SuspendedEV/EVSE, Finishing) → OCCUPIED. Include il fix del
 *    bug attuale `all(PREPARING)→AVAILABLE` (auto attaccata, stazione mostrata libera):
 *    cambio VOLUTO, coperto dai golden test.
 */
export function computeAggregate(
  physical: PhysicalStatusValues | undefined,
  session?: SessionStateValues | null,
): AggregateStateValues {
  if (physical === PhysicalStatus.Faulted || physical === PhysicalStatus.Unavailable) {
    return AggregateState.UNAVAILABLE;
  }
  if (session === SessionState.RESERVED || physical === PhysicalStatus.Reserved) {
    return AggregateState.RESERVED;
  }
  if (physical === PhysicalStatus.Available && !(session != null && LIVE_SESSION.has(session))) {
    return AggregateState.AVAILABLE;
  }
  if (physical === undefined) return AggregateState.UNAVAILABLE;
  return AggregateState.OCCUPIED;
}

/**
 * Combina i contributi dei membri (connettori→stazione, stazioni→installazione).
 * Stessa semantica dell'attuale `calculateState`: "almeno uno libero = libero";
 * il misto eterogeneo è OCCUPIED. Un membro OFFLINE non spegne il padre (la
 * connettività del padre è una dimensione a parte: `applyConnectivity`); tutti
 * i membri OFFLINE → OFFLINE.
 */
export function combineAggregates(members: readonly AggregateStateValues[]): AggregateStateValues {
  if (members.length === 0) return AggregateState.UNAVAILABLE;
  if (members.includes(AggregateState.AVAILABLE)) return AggregateState.AVAILABLE;
  const reachable = members.filter((m) => m !== AggregateState.OFFLINE);
  if (reachable.length === 0) return AggregateState.OFFLINE;
  if (reachable.every((m) => m === AggregateState.UNAVAILABLE)) return AggregateState.UNAVAILABLE;
  if (reachable.every((m) => m === AggregateState.RESERVED)) return AggregateState.RESERVED;
  return AggregateState.OCCUPIED;
}

/**
 * Dimensione connettività (RFC §5.6): gateway/stazione irraggiungibile → OFFLINE,
 * qualunque cosa dicano i membri (la nostra visibilità è cambiata, non loro).
 */
export function applyConnectivity(aggregate: AggregateStateValues, online: boolean): AggregateStateValues {
  return online ? aggregate : AggregateState.OFFLINE;
}

/**
 * ALLOWLIST dei soli status CP-level (`connectorId = 0`) che valgono come GUASTO DI STAZIONE.
 *
 * In OCPP il `connectorId = 0` è ambiguo per costruzione: significa "la colonnina nel suo insieme",
 * e i firmware ci mettono dentro sia l'aggregato dei connettori sia i problemi generali. L'aggregato
 * lo sappiamo già derivare dai connettori — quello non ci serve, e prenderlo per buono farebbe
 * decidere a un dato ambiguo ciò che sappiamo calcolare da dati precisi. Quello che i connettori
 * NON possono dirci è il guasto della colonnina intera: solo quello si onora, e solo da questa
 * lista chiusa. Faulted = guasto dichiarato; Unavailable = fuori servizio dichiarato.
 * Tutto il resto (Available, Preparing, Charging, …) a livello CP non significa niente di
 * asseribile → si ignora.
 */
export const STATION_FAULT_PHYSICAL: readonly PhysicalStatusValues[] = Object.freeze([
  PhysicalStatus.Faulted,
  PhysicalStatus.Unavailable,
]);

/** True se lo status CP-level è un guasto di stazione (unica definizione, vedi STATION_FAULT_PHYSICAL). */
export function isStationFaultPhysical(physical: PhysicalStatusValues | undefined | null): boolean {
  return physical != null && STATION_FAULT_PHYSICAL.includes(physical);
}

/**
 * Override asimmetrico del fisico CP-level (StatusNotification connectorId=0,
 * decisione §10.18): solo i guasti dell'allowlist forzano UNAVAILABLE; Available a
 * livello colonnina NON forza nulla (decidono i connettori).
 */
export function applyStationPhysicalOverride(
  aggregate: AggregateStateValues,
  stationPhysical: PhysicalStatusValues | undefined,
): AggregateStateValues {
  return isStationFaultPhysical(stationPhysical) ? AggregateState.UNAVAILABLE : aggregate;
}

/**
 * Ordine di USABILITÀ dei fisici, dal più libero al meno: è la stessa scala di precedenza di
 * `combineAggregates` ("almeno uno libero = libero"), espressa nel vocabolario fisico.
 */
const PHYSICAL_BY_USABILITY: readonly PhysicalStatusValues[] = Object.freeze([
  PhysicalStatus.Available,
  PhysicalStatus.Preparing,
  PhysicalStatus.Charging,
  PhysicalStatus.SuspendedEV,
  PhysicalStatus.SuspendedEVSE,
  PhysicalStatus.Finishing,
  PhysicalStatus.Reserved,
  PhysicalStatus.Unavailable,
  PhysicalStatus.Faulted,
]);

/**
 * Fisico di STAZIONE come sintesi dei fisici dei suoi connettori: vince il connettore più usabile.
 *
 * `station.innerState` è il fisico del connettore 0 — ma quel livello esiste solo dove la colonnina
 * parla un protocollo diretto (OCPP `connectorId = 0`). Le stazioni che arrivano dal ROAMING
 * (OCPI/OICP) non ce l'hanno, e lì il fisico di stazione È per definizione l'aggregato degli
 * `innerState` dei suoi connettori. Nessun membro noto → `undefined`: niente da asserire, non
 * "stato sconosciuto" (il chiamante tiene il valore già in colonna).
 */
export function combinePhysicalStatuses(
  members: readonly PhysicalStatusValues[],
): PhysicalStatusValues | undefined {
  let best = -1;
  for (const m of members) {
    const rank = PHYSICAL_BY_USABILITY.indexOf(m);
    if (rank >= 0 && (best < 0 || rank < best)) best = rank;
  }
  return best < 0 ? undefined : PHYSICAL_BY_USABILITY[best];
}

// ---------------------------------------------------------------------------
// Conversione fra le due rappresentazioni CORRENTI dello stato: il vocabolario a
// stringhe (Redis, eventi, calcolo) e il codice numerico `State` (colonne di stato
// dell'anagrafica, payload verso le app). Entrambe vive, entrambe scritte.
// ---------------------------------------------------------------------------

/**
 * Stato LOGICO del connettore (`State` numerico) come FUSIONE di fisico ⊕ sessione — la foglia.
 * Vocabolario ricco (Charging distinto da Occupied, a differenza di `aggregateToStateCode`
 * che è la sintesi per la mappa). Precedenza, dall'alto:
 *  1. Guasto (Faulted/Unavailable) → UNAVAILABLE: la sicurezza vince su tutto, sessione inclusa.
 *  2. La SESSIONE esprime ciò che il fisico non sa (Reserved/Occupied/Pending): la sua verità guida.
 *  3. Nessuna sessione viva (o ENDING/ENDED) → guida il FISICO.
 * Sostituisce la derivazione fisico-secca che ignorava la sessione (da lì i "riservato/occupato
 * spariti"). Va ricalcolato edge-triggered ai due cambi.
 */
export function computeConnectorState(
  physical: PhysicalStatusValues | undefined,
  session?: SessionStateValues | null,
): StateValues {
  if (physical === PhysicalStatus.Faulted || physical === PhysicalStatus.Unavailable) return State.UNAVAILABLE;
  switch (session) {
    case SessionState.RESERVED: return State.RESERVED;
    case SessionState.PENDING: return State.PENDING;
    case SessionState.PENDING_AUTH: return State.PENDING_AUTH;
    case SessionState.ACTIVE: return State.CHARGING;
    case SessionState.OCCUPIED: return State.OCCUPIED;
    // ENDING/ENDED/null → nessuna sessione viva: decide il fisico sotto.
  }
  switch (physical) {
    case PhysicalStatus.Available: return State.AVAILABLE;
    case PhysicalStatus.Preparing: return State.PREPARING;
    case PhysicalStatus.Charging:
    case PhysicalStatus.SuspendedEV:
    case PhysicalStatus.SuspendedEVSE: return State.CHARGING;
    case PhysicalStatus.Finishing: return State.OCCUPIED;
    case PhysicalStatus.Reserved: return State.RESERVED;
    default: return State.UNAVAILABLE; // Faulted/Unavailable già sopra; undefined → mai "disponibile"
  }
}

/**
 * UNICO arbitro di `connector.state`: dato tutto ciò che si sa di un connettore, dice quale valore
 * va persistito, oppure `undefined` = NON scrivere. Entrambi i writer della colonna (l'emit
 * edge-triggered su evento e il flush a batch) passano di qui: due regole diverse sulla stessa
 * colonna si alternavano a seconda di chi arrivava ultimo.
 *
 * Precedenza:
 *  1. Stazione irraggiungibile → UNAVAILABLE: l'ultimo fisico noto è per definizione vecchio.
 *  2. Stato sessione noto → macchina completa fisico⊕sessione.
 *  3. Connettore LEGATO a una sessione (`currentSessionId`) ma stato sessione non in cache → non
 *     scrivere: dedurre dal solo fisico sovrascriverebbe uno stato-sessione valido.
 *  4. Nessuna sessione e fisico ignoto → non scrivere: mai asserire su assenza d'informazione.
 *  5. Altrimenti decide il fisico.
 */
export function resolveConnectorState(
  physical: PhysicalStatusValues | undefined,
  session: SessionStateValues | null | undefined,
  hasBoundSession: boolean,
  online: boolean,
): StateValues | undefined {
  if (!online) return State.UNAVAILABLE;
  if (session != null) return computeConnectorState(physical, session);
  if (hasBoundSession) return undefined;
  if (physical === undefined) return undefined;
  return computeConnectorState(physical, null);
}

/** Aggregato → codice numerico di colonna. OFFLINE→UNAVAILABLE (il numerico non distingue i due). */
export function aggregateToStateCode(aggregate: AggregateStateValues): StateValues {
  switch (aggregate) {
    case AggregateState.AVAILABLE: return State.AVAILABLE;
    case AggregateState.RESERVED: return State.RESERVED;
    case AggregateState.OCCUPIED: return State.OCCUPIED;
    case AggregateState.UNAVAILABLE:
    case AggregateState.OFFLINE:
    default: return State.UNAVAILABLE;
  }
}

/**
 * Colonne di stato dell'anagrafica → aggregato: l'inverso di `aggregateToStateCode`,
 * che collassa OFFLINE e UNAVAILABLE sullo stesso numero. `online === false` è l'unica
 * codifica di OFFLINE e vince su qualunque `state` — inverso esatto di `applyConnectivity`,
 * e raddrizza anche le righe lasciate incoerenti da scritture precedenti. Chi non ha la
 * colonna `online` (installation) omette il parametro: a quel livello OFFLINE non è
 * distinguibile da UNAVAILABLE.
 *
 * I numerici di livello connettore (CHARGING/PREPARING/PENDING/PENDING_AUTH) non sono
 * aggregati validi ma significano comunque "non libero" → OCCUPIED. Valore fuori enum →
 * undefined: nessuno stato inventato.
 */
export function stateCodeToAggregate(state: number | undefined, online = true): AggregateStateValues | undefined {
  if (!online) return AggregateState.OFFLINE;
  switch (state) {
    case State.AVAILABLE: return AggregateState.AVAILABLE;
    case State.RESERVED: return AggregateState.RESERVED;
    case State.CHARGING:
    case State.OCCUPIED:
    case State.PREPARING:
    case State.PENDING:
    case State.PENDING_AUTH: return AggregateState.OCCUPIED;
    case State.UNAVAILABLE: return AggregateState.UNAVAILABLE;
    default: return undefined;
  }
}

/**
 * Lifecycle sessione → codice numerico, per la colonna `session.state`.
 * ENDING/ENDED → undefined = NON scrivere il numerico: a fine sessione lo stop è
 * implicito, la riga tiene l'ultimo stato e fa fede `endedAt`.
 */
export function sessionStateToStateCode(session: SessionStateValues): StateValues | undefined {
  switch (session) {
    case SessionState.RESERVED: return State.RESERVED;
    case SessionState.PENDING: return State.PENDING;
    case SessionState.PENDING_AUTH: return State.PENDING_AUTH;
    case SessionState.ACTIVE: return State.CHARGING;
    case SessionState.OCCUPIED: return State.OCCUPIED;
    case SessionState.ENDING:
    case SessionState.ENDED:
    default: return undefined;
  }
}

/**
 * Codice numerico → lifecycle sessione (inverso di `sessionStateToStateCode`).
 * Valori che una sessione non dovrebbe avere (AVAILABLE, UNAVAILABLE, PREPARING)
 * → undefined: righe da flaggare, non da indovinare.
 */
export function stateCodeToSessionState(stateCode: StateValues): SessionStateValues | undefined {
  switch (stateCode) {
    case State.RESERVED: return SessionState.RESERVED;
    case State.PENDING: return SessionState.PENDING;
    case State.PENDING_AUTH: return SessionState.PENDING_AUTH;
    case State.CHARGING: return SessionState.ACTIVE;
    case State.OCCUPIED: return SessionState.OCCUPIED;
    default: return undefined;
  }
}

/**
 * UNICO derivatore dello stato sessione dalle colonne dell'anagrafica.
 *
 * `session.state` È lo stato della sessione: non esiste una colonna dedicata al vocabolario
 * stringa, che è una VISTA sul numerico e vive solo in memoria, su Redis e sul bus. Chiunque
 * debba conoscere lo stato di una sessione passa di qui, così la derivazione non si sparpaglia.
 *
 * `endedAt` vince su tutto: nel modello legacy lo stop è implicito — la riga conserva l'ultimo
 * stato utile e la chiusura la certifica `endedAt`, non un valore di `state`.
 */
export function sessionStateOf(
  stateCode: StateValues | null | undefined,
  endedAt?: Date | string | null,
): SessionStateValues | undefined {
  if (endedAt) return SessionState.ENDED;
  return stateCode == null ? undefined : stateCodeToSessionState(stateCode);
}

/**
 * Campi diagnostici del backoffice, additivi e sempre opzionali: lo stato LIQUIDO (`stateLive`,
 * da Redis — quello vero, che cambia a ogni evento) affiancato a quello PERSISTITO
 * (`statePersisted`, le colonne anagrafiche — che si aggiornano al ritmo del flush).
 * `state` resta invariato.
 *
 * Assente ≠ vuoto: campo mancante = non letto (Redis giù, entità non ancora calda), non "nessuno stato".
 */
export interface IEvseStateDiagnostics {
  stateLive?: AggregateStateValues;
  statePersisted?: AggregateStateValues;
  /** ISO del momento in cui l'evento che ha prodotto `stateLive` è avvenuto. */
  stateLiveAt?: string;
  /**
   * Fisico CP-level LIVE (`station:phys`). Da quando il CP-level è normato, quella chiave contiene
   * SOLO i guasti dichiarati dalla colonnina: assente = nessun guasto, non "stato sconosciuto".
   */
  innerStateLive?: PhysicalStatusValues;
  /** Il corrispettivo in colonna (`station.innerState`), che si aggiorna al ritmo del flush. */
  innerStatePersisted?: string | null;
}

/**
 * Come sopra ma per il CONNETTORE, dove i due campi hanno tipi diversi: lo stato è il numerico
 * `State` (fusione fisico⊕sessione, non un aggregato) e il fisico è lo StatusNotification puro.
 * Stessi NOMI di campo della versione aggregata, così la UI li rende con lo stesso componente.
 *
 * `stateLive` NON è una lettura secca da Redis: è ciò che il service deciderebbe ADESSO, ricalcolato
 * con la stessa `resolveConnectorState` che usano i suoi due writer. È l'unico modo di renderlo
 * confrontabile col persistito: la colonna è il risultato di quella funzione, non del fisico grezzo.
 */
export interface IEvseConnectorDiagnostics {
  stateLive?: StateValues;
  statePersisted?: StateValues;
  stateLiveAt?: string;
  innerStateLive?: PhysicalStatusValues;
  innerStatePersisted?: string | null;
}
